// src/hooks/results/useMeasurementResults.js
import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { documentId } from "firebase/firestore";
import { mapMeasurementDocToRow } from "./mapMeasurementRow";
import {
  getInitialRealtimeListenLimit,
  getNextRealtimeListenLimit,
  isNullishSubjectId,
  selectRealtimeMeasurementDocs,
  shouldExpandRealtimeListenLimit,
} from "./loadStagedMeasurementDocs";

export function useMeasurementResults(
  db,
  uid,
  { collectionName, context, pageSize = 50, subjectId = null },
) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(!!db && !!uid);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedSnapshotRef = useRef(false);

  const colRef = useMemo(() => {
    if (!db || !uid) return null;
    return collection(db, "measurements", uid, collectionName);
  }, [db, uid, collectionName]);

  const activeSubject = useMemo(
    () => (subjectId ? String(subjectId).trim() : ""),
    [subjectId],
  );
  const hasClientFilter = !activeSubject;
  const initialListenLimit = useMemo(
    () => getInitialRealtimeListenLimit(pageSize, hasClientFilter),
    [pageSize, hasClientFilter],
  );
  const [listenLimit, setListenLimit] = useState(initialListenLimit);

  useEffect(() => {
    hasLoadedSnapshotRef.current = false;
    setListenLimit(getInitialRealtimeListenLimit(pageSize, hasClientFilter));
    setLoading(!!colRef);
    setLoadingMore(false);
    setHasMore(false);
    setError(null);
  }, [colRef, context, activeSubject, hasClientFilter]);

  useEffect(() => {
    if (!colRef) {
      setRows([]);
      setLoading(false);
      setLoadingMore(false);
      setHasMore(false);
      setError(null);
      return;
    }

    let cancelled = false;

    if (!hasLoadedSnapshotRef.current) {
      setLoading(true);
      setLoadingMore(false);
    }
    setError(null);

    const baseConstraints = [where("appMode", "==", context)];
    if (activeSubject) {
      baseConstraints.push(where("subjectId", "==", activeSubject));
    }

    baseConstraints.push(orderBy(documentId(), "desc"));

    const clientFilter = activeSubject
      ? null
      : (data) => isNullishSubjectId(data?.subjectId);

    const qy = query(colRef, ...baseConstraints, limit(listenLimit));

    const unsub = onSnapshot(
      qy,
      (snap) => {
        if (cancelled) return;

        const docs = selectRealtimeMeasurementDocs(
          snap.docs,
          pageSize,
          clientFilter,
        );

        setRows(docs.map((doc) => mapMeasurementDocToRow(doc, context)));
        hasLoadedSnapshotRef.current = true;
        setLoading(false);
        setHasMore(snap.size >= listenLimit);

        const needsExpansion = shouldExpandRealtimeListenLimit(
          snap.size,
          listenLimit,
          docs.length,
          pageSize,
        );

        setLoadingMore(needsExpansion);

        if (needsExpansion) {
          setListenLimit((currentLimit) =>
            getNextRealtimeListenLimit(
              currentLimit,
              pageSize,
              hasClientFilter,
            ),
          );
        }
      },
      (err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
        setLoadingMore(false);
        setHasMore(false);
      },
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [colRef, pageSize, context, activeSubject, listenLimit, hasClientFilter]);

  return { rows, loading, loadingMore, hasMore, error };
}
